import type { CellPosition } from "./GridCell";

export type HandleCorner = "top-left" | "top-right" | "bottom-left" | "bottom-right";

export interface CellFocusNotification {
    isFocused: boolean;
    isEditing: boolean;
    showSelectionHandle: boolean;
    handleCorner: HandleCorner;
    focusDom: boolean;
}

export interface CellFocusNotifyParams {
    focusKeys: Set<string>;
    editingKey: string | null;
    handleKey: string | null;
    handleCorner: HandleCorner;
    focusDOMKey: string | null;
}

export function createCellFocusStore() {
    const subscribers = new Map<string, (state: CellFocusNotification) => void>();

    // Diff tracking — only notify cells whose state actually changed
    let prevFocusKeys = new Set<string>();
    let prevEditingKey: string | null = null;
    let prevHandleKey: string | null = null;
    // Snapshot of the last params for cells that subscribe late (virtualization)
    let lastParams: CellFocusNotifyParams | null = null;

    // moveDomFocus false notifies the cells without moving the DOM focus to the
    // focusDOMKey cell (it remains the target of the cells mounting later and
    // of triggerRefocus). Returns true when a mounted cell was asked to take
    // the DOM focus.
    function notify(params: CellFocusNotifyParams, moveDomFocus: boolean = true): boolean {
        lastParams = params;
        const { focusKeys, editingKey, handleKey, handleCorner, focusDOMKey } = params;

        // Only the cells that could have changed state
        const toNotify = new Set([...Array.from(focusKeys), ...Array.from(prevFocusKeys)]);
        if (editingKey) toNotify.add(editingKey);
        if (prevEditingKey) toNotify.add(prevEditingKey);
        if (handleKey) toNotify.add(handleKey);
        if (prevHandleKey) toNotify.add(prevHandleKey);

        for (const key of Array.from(toNotify)) {
            subscribers.get(key)?.({
                isFocused: focusKeys.has(key),
                isEditing: key === editingKey,
                showSelectionHandle: key === handleKey,
                handleCorner,
                focusDom: moveDomFocus && key === focusDOMKey,
            });
        }

        prevFocusKeys = focusKeys;
        prevEditingKey = editingKey;
        prevHandleKey = handleKey;

        return moveDomFocus && focusDOMKey !== null && subscribers.has(focusDOMKey);
    }

    function triggerRefocus() {
        if (!lastParams?.focusDOMKey) return;
        const { focusDOMKey, focusKeys, editingKey, handleKey, handleCorner } = lastParams;
        const cb = subscribers.get(focusDOMKey);
        if (cb) {
            cb({
                isFocused: focusKeys.has(focusDOMKey),
                isEditing: focusDOMKey === editingKey,
                showSelectionHandle: focusDOMKey === handleKey,
                handleCorner,
                focusDom: true,
            });
        }
    }

    function subscribe(key: string, cb: (state: CellFocusNotification) => void): () => void {
        subscribers.set(key, cb);
        // Immediately apply current state so cells mounting after initial render
        // (e.g. via virtualization) get the right state without waiting for next change
        if (lastParams) {
            const { focusKeys, editingKey, handleKey, handleCorner, focusDOMKey } = lastParams;
            cb({
                isFocused: focusKeys.has(key),
                isEditing: key === editingKey,
                showSelectionHandle: key === handleKey,
                handleCorner,
                // Auto-focus DOM if this cell is the intended focus target (e.g. mounting after
                // being outside the virtual window when Tab was pressed)
                focusDom: key === focusDOMKey,
            });
        }
        return () => subscribers.delete(key);
    }

    return { notify, subscribe, triggerRefocus };
}

export type CellFocusStore = ReturnType<typeof createCellFocusStore>;

// The grid state the cell focus is derived from (a subset of the data store state)
export interface CellFocusSourceState {
    focusPos: CellPosition[] | null;
    editingPos: CellPosition | null;
    selectionAnchor: CellPosition | null;
}

// A store holding the grid state (the zustand data store)
export interface CellFocusSource<S extends CellFocusSourceState> {
    getState: () => S;
    subscribe: (listener: (state: S, prevState: S) => void) => () => void;
}

// Hooks around the focus changes which may move the DOM focus to a cell
export interface DomFocusHooks {
    // Called right before the cells are notified of the focus change
    beforeMove: () => void;
    // Called once a cell has taken the DOM focus (it is then document.activeElement)
    afterMove: () => void;
}

// syncCellFocus keeps the cells in sync with the grid state: they are notified
// whenever the focused, edited or anchor cell changes. The DOM focus only
// moves when the focused or edited cell changes: a click sets the selection
// anchor right before the new focus, and giving the DOM focus back to the
// previously focused cell in between would scroll the grid to it (the clicked
// cell then leaves the view or slides under the frozen columns).
// Returns the unsubscribe function.
export function syncCellFocus<S extends CellFocusSourceState>(
    cellFocusStore: CellFocusStore,
    source: CellFocusSource<S>,
    computeParams: (state: S) => CellFocusNotifyParams,
    domFocusHooks?: DomFocusHooks,
): () => void {
    // Seed with current state so cells mounting later (virtualization) get the right state
    cellFocusStore.notify(computeParams(source.getState()));

    return source.subscribe((state, prevState) => {
        const focusChanged = state.focusPos !== prevState.focusPos || state.editingPos !== prevState.editingPos;
        if (!focusChanged) {
            if (state.selectionAnchor !== prevState.selectionAnchor) {
                cellFocusStore.notify(computeParams(state), false);
            }
            return;
        }
        domFocusHooks?.beforeMove();
        if (cellFocusStore.notify(computeParams(state))) {
            domFocusHooks?.afterMove();
        }
    });
}
